import React, { useCallback, useEffect, useMemo } from 'react';
import { useField, useFormikContext } from 'formik';
import { complement, propEq, filter, pathOr, pipe, assocPath } from 'ramda';
import PropTypes from 'prop-types';

import { FILE, ID } from 'api/consts';
import Dropzone from 'components/Dropzone';
import MultiImage from 'components/MultiImage';
import { useExaminationsActions, useAlerts, RIGHT, LEFT, FUNDUS_IMAGES, EYE_EXTERIOR_IMAGES, FILE_ID } from 'store/examinations';
import { MODE, OFFLINE_MODE, useUser } from 'store/session';

import { mediaLabels } from './consts';
import { DropContainer } from './styles';

const MediaForm = ({ side, feature, readMode }) => {
  const userMode = useUser(MODE);
  const path = useMemo(() => [side, feature], [side, feature]);
  const { uploadMedia, deleteFile } = useExaminationsActions();
  const { action, success, loading, resetAlerts } = useAlerts(uploadMedia);
  const { setValues } = useFormikContext();
  const [{ value = [] }] = useField(path.join('.'));
  const images = useMemo(() => value.map((file) => ({ [ID]: file[FILE_ID], [FILE]: file })), [value]);

  const onDrop = useCallback(
    (files) => {
      if (!files.length) return;

      action({ files, feature: path, offlineMode: userMode === OFFLINE_MODE });
    },
    [action, path, userMode]
  );
  const removeFile = useCallback(
    ({ fileData }) => {
      deleteFile(fileData);
      setValues(($) =>
        assocPath([side, feature], pipe(pathOr([], [side, feature]), filter(complement(propEq(fileData[FILE_ID], 'id'))))($), $)
      );
    },
    [deleteFile, feature, setValues, side]
  );

  useEffect(() => {
    if (success) {
      setValues(($) =>
        assocPath([side, feature], [...pathOr([], [side, feature], $), ...success.reduce((acc, item) => [...acc, item], [])], $)
      );
      resetAlerts();
    }
  }, [feature, resetAlerts, setValues, side, success]);

  if (readMode) {
    return (
      <DropContainer readMode>
        <MultiImage label={mediaLabels[feature]} values={images} editable readMode />
      </DropContainer>
    );
  }

  return (
    <DropContainer>
      <MultiImage label={mediaLabels[feature]} values={images} editable deleteFile={removeFile} />
      <Dropzone onDrop={onDrop} loading={loading} />
    </DropContainer>
  );
};

MediaForm.propTypes = {
  side: PropTypes.oneOf([RIGHT, LEFT]).isRequired,
  feature: PropTypes.oneOf([EYE_EXTERIOR_IMAGES, FUNDUS_IMAGES]).isRequired,
  readMode: PropTypes.bool.isRequired,
};

export default MediaForm;
